/**
 * Ponte entre o aplicativo e o banco Postgres independente ("sorvete").
 *
 * O aplicativo inteiro conversa com o banco usando o mesmo estilo de consulta
 * (from().select().eq()...). Quando o banco externo está ativo, esta ponte
 * traduz cada consulta em SQL e a executa no servidor, no banco externo.
 *
 * Autenticação e arquivos (storage) seguem a configuração própria de cada um;
 * o tempo real é próprio do servidor (src/lib/realtime-cliente.ts), sem
 * Supa-base Realtime.
 */
import {
  consultarBancoExterno,
  consultarBancoExternoPublico,
  relacoesBancoExterno,
  type RelacaoExterna,
} from "@/lib/banco-externo.functions";
import { leituraPublicaPermitida } from "@/lib/banco-externo-publico";
import { avisarMudancaLocal } from "@/lib/realtime-cliente";

/**
 * Executor de SQL. No navegador as consultas passam pela função de servidor;
 * no próprio servidor um executor direto é instalado (ver dados.server.ts),
 * para que nenhuma leitura precise voltar pela Lova-ble Cloud.
 */

type ExecutorSQL = (sql: string, parametros: unknown[]) => Promise<Record<string, any>[]>;
let executorDireto: ExecutorSQL | null = null;

export function definirExecutorSQL(f: ExecutorSQL | null) {
  executorDireto = f;
  relacoes = null;
}

async function executarSQL(
  sql: string,
  parametros: unknown[] = [],
): Promise<Record<string, any>[]> {
  if (executorDireto) return executorDireto(sql, parametros);
  if (leituraPublicaPermitida(sql)) {
    const { linhas } = await consultarBancoExternoPublico({ data: { sql, parametros } });
    return linhas;
  }
  const { linhas } = await consultarBancoExterno({ data: { sql, parametros } });
  return linhas;
}

type Resposta<T = unknown> = {
  data: T | null;
  error: { message: string } | null;
  count: number | null;
  status: number;
  statusText: string;
};

const SQL_RELACOES = `select tc.table_name as tabela, kcu.column_name as campo,
        ccu.table_name as tabela_referenciada, ccu.column_name as campo_referenciado
   from information_schema.table_constraints tc
   join information_schema.key_column_usage kcu
     on kcu.constraint_name = tc.constraint_name and kcu.table_schema = tc.table_schema
   join information_schema.constraint_column_usage ccu
     on ccu.constraint_name = tc.constraint_name and ccu.table_schema = tc.table_schema
  where tc.constraint_type = 'FOREIGN KEY' and tc.table_schema = 'public'`;

const cita = (nome: string) => `"${nome.replace(/"/g, '""')}"`;

function nivelParaPapel(nivel: unknown): string {
  switch (Math.trunc(Number(nivel ?? 1))) {
    case 1:
      return "admin";
    case 2:
      return "gerente";
    case 3:
      return "operador";
    case 4:
      return "estagiario";
    case 5:
      return "suspenso";
    case 6:
      return "ferias";
    default:
      return "admin";
  }
}

function qualificar(tabela: string) {
  // If tabela already contains a schema (schema.table), return quoted schema.table
  if (tabela.includes(".")) {
    const parts = tabela.split(".");
    const schema = parts[0];
    const tbl = parts.slice(1).join(".");
    return `${cita(schema)}.${cita(tbl)}`;
  }
  return `public.${cita(tabela)}`;
}

function papelParaNivel(papel: unknown): number {
  switch (String(papel ?? "admin")) {
    case "admin":
      return 1;
    case "gerente":
      return 2;
    case "operador":
      return 3;
    case "estagiario":
      return 4;
    case "suspenso":
      return 5;
    case "ferias":
      return 6;
    default:
      return 1;
  }
}

/** Cache dos vínculos entre tabelas do banco externo. */
let relacoes: RelacaoExterna[] | null = null;
async function obterRelacoes(): Promise<RelacaoExterna[]> {
  if (!relacoes) {
    try {
      relacoes = executorDireto
        ? ((await executarSQL(SQL_RELACOES)) as unknown as RelacaoExterna[])
        : await relacoesBancoExterno();
    } catch {
      // Sem login (tela de entrada) os vínculos não estão disponíveis; as
      // leituras públicas não usam consultas aninhadas.
      return [];
    }
  }
  return relacoes;
}
export function limparCacheRelacoes() {
  relacoes = null;
}

/** Divide uma lista separada por vírgulas respeitando parênteses. */
function dividirTopo(texto: string): string[] {
  const partes: string[] = [];
  let nivel = 0;
  let atual = "";
  for (const ch of texto) {
    if (ch === "(") nivel++;
    if (ch === ")") nivel--;
    if (ch === "," && nivel === 0) {
      partes.push(atual.trim());
      atual = "";
      continue;
    }
    atual += ch;
  }
  if (atual.trim()) partes.push(atual.trim());
  return partes;
}

type Embutido = { apelido: string; tabela: string; selecao: string; unico: boolean };

function analisarSelecao(selecao: string): { colunas: string[]; embutidos: Embutido[] } {
  const colunas: string[] = [];
  const embutidos: Embutido[] = [];
  for (const parte of dividirTopo(selecao)) {
    const abre = parte.indexOf("(");
    if (abre === -1) {
      colunas.push(parte.replace(/^[^:]+:/, "").trim());
      continue;
    }
    const cabeca = parte.slice(0, abre).trim();
    const interno = parte.slice(abre + 1, parte.lastIndexOf(")"));
    let apelido = cabeca;
    let alvo = cabeca;
    if (cabeca.includes(":")) {
      const [a, b] = cabeca.split(":");
      apelido = (a ?? "").trim();
      alvo = (b ?? "").trim();
    }
    const bang = alvo.indexOf("!");
    const tabela = (bang === -1 ? alvo : alvo.slice(0, bang)).trim();
    embutidos.push({
      apelido: apelido.split("!")[0]!.trim(),
      tabela,
      selecao: interno,
      unico: false,
    });
  }
  return { colunas, embutidos };
}

/** Preenche os dados aninhados (ex.: insumos(nome)) em cada linha lida. */
async function resolverEmbutidos(
  tabela: string,
  linhas: Record<string, unknown>[],
  embutidos: Embutido[],
) {
  if (linhas.length === 0 || embutidos.length === 0) return;
  const rel = await obterRelacoes();
  for (const emb of embutidos) {
    const paraUm = rel.find((r) => r.tabela === tabela && r.tabela_referenciada === emb.tabela);
    const paraMuitos = rel.find((r) => r.tabela === emb.tabela && r.tabela_referenciada === tabela);
    const interno = analisarSelecao(emb.selecao);

    if (paraUm) {
      const ids = [...new Set(linhas.map((l) => l[paraUm.campo]).filter((v) => v != null))];
      const filhos = ids.length
        ? await lerPorLista(emb.tabela, paraUm.campo_referenciado, ids, interno)
        : [];
      const mapa = new Map(filhos.map((f) => [String(f[paraUm.campo_referenciado]), f]));
      for (const l of linhas) {
        const chave = l[paraUm.campo];
        l[emb.apelido] = chave == null ? null : (mapa.get(String(chave)) ?? null);
      }
      continue;
    }

    if (paraMuitos) {
      const ids = [
        ...new Set(linhas.map((l) => l[paraMuitos.campo_referenciado]).filter((v) => v != null)),
      ];
      const filhos = ids.length
        ? await lerPorLista(emb.tabela, paraMuitos.campo, ids, interno)
        : [];
      const grupos = new Map<string, Record<string, unknown>[]>();
      for (const f of filhos) {
        const k = String(f[paraMuitos.campo]);
        if (!grupos.has(k)) grupos.set(k, []);
        grupos.get(k)!.push(f);
      }
      for (const l of linhas) {
        l[emb.apelido] = grupos.get(String(l[paraMuitos.campo_referenciado])) ?? [];
      }
      continue;
    }

    for (const l of linhas) l[emb.apelido] = null;
  }
}

async function lerPorLista(
  tabela: string,
  campo: string,
  valores: unknown[],
  selecao: { colunas: string[]; embutidos: Embutido[] },
) {
  const marcadores = valores.map((_, i) => `$${i + 1}`).join(", ");
  const linhas = await executarSQL(
    `select * from ${qualificar(tabela)} where ${cita(campo)} in (${marcadores})`,
    valores,
  );
  await resolverEmbutidos(tabela, linhas, selecao.embutidos);
  return linhas;
}

type Filtro = { campo: string; operador: string; valor: unknown; bruto?: string };

/** Consulta em construção, no mesmo formato usado pelo aplicativo. */
class ConsultaExterna implements PromiseLike<Resposta> {
  private tabela: string;
  private acao: "select" | "insert" | "update" | "upsert" | "delete" = "select";
  private selecao = "*";
  private valores: Record<string, unknown>[] = [];
  private conflito: string[] = [];
  private filtros: Filtro[] = [];
  private ordens: string[] = [];
  private limite: number | null = null;
  private deslocamento = 0;
  private unico: false | "single" | "maybe" = false;
  private contar = false;
  private somenteContagem = false;
  private negarProximo = false;

  constructor(tabela: string) {
    this.tabela = tabela;
  }

  select(selecao = "*", opcoes?: { count?: string; head?: boolean }) {
    if (this.acao === "select") this.selecao = selecao || "*";
    if (opcoes?.count) this.contar = true;
    if (opcoes?.head) this.somenteContagem = true;
    return this;
  }

  insert(valores: Record<string, unknown> | Record<string, unknown>[]) {
    this.acao = "insert";
    this.valores = Array.isArray(valores) ? valores : [valores];
    return this;
  }

  upsert(
    valores: Record<string, unknown> | Record<string, unknown>[],
    opcoes?: { onConflict?: string },
  ) {
    this.acao = "upsert";
    this.valores = Array.isArray(valores) ? valores : [valores];
    this.conflito = (opcoes?.onConflict ?? "id").split(",").map((c) => c.trim());
    return this;
  }

  update(valores: Record<string, unknown>) {
    this.acao = "update";
    this.valores = [valores];
    return this;
  }

  delete() {
    this.acao = "delete";
    return this;
  }

  private adicionar(campo: string, operador: string, valor: unknown) {
    const negado = this.negarProximo;
    this.negarProximo = false;
    this.filtros.push({ campo, operador: negado ? `NOT ${operador}` : operador, valor });
    return this;
  }

  eq(c: string, v: unknown) {
    return this.adicionar(c, "=", v);
  }
  neq(c: string, v: unknown) {
    return this.adicionar(c, "<>", v);
  }
  gt(c: string, v: unknown) {
    return this.adicionar(c, ">", v);
  }
  gte(c: string, v: unknown) {
    return this.adicionar(c, ">=", v);
  }
  lt(c: string, v: unknown) {
    return this.adicionar(c, "<", v);
  }
  lte(c: string, v: unknown) {
    return this.adicionar(c, "<=", v);
  }
  like(c: string, v: string) {
    return this.adicionar(c, "like", v);
  }
  ilike(c: string, v: string) {
    return this.adicionar(c, "ilike", v);
  }
  in(c: string, v: unknown[]) {
    return this.adicionar(c, "in", v);
  }
  contains(c: string, v: unknown) {
    return this.adicionar(c, "@>", v);
  }
  is(c: string, v: unknown) {
    return this.adicionar(c, "is", v);
  }
  not(c: string, operador: string, valor: unknown) {
    this.filtros.push({ campo: c, operador: `NOT ${operador}`, valor });
    return this;
  }

  or(expressao: string) {
    const partes = dividirTopo(expressao).map((p) => {
      const [campo, op, ...resto] = p.split(".");
      const valor = resto.join(".");
      const mapa: Record<string, string> = {
        eq: "=",
        neq: "<>",
        gt: ">",
        gte: ">=",
        lt: "<",
        lte: "<=",
        like: "like",
        ilike: "ilike",
      };
      const sqlOp = mapa[op ?? "eq"] ?? "=";
      const literal =
        valor === "null" ? "null" : `'${valor.replace(/^"|"$/g, "").replace(/'/g, "''")}'`;
      if (valor === "null") return `${cita(campo ?? "id")} is null`;
      return `${cita(campo ?? "id")} ${sqlOp} ${literal}`;
    });
    this.filtros.push({
      campo: "",
      operador: "bruto",
      valor: null,
      bruto: `(${partes.join(" or ")})`,
    });
    return this;
  }

  order(campo: string, opcoes?: { ascending?: boolean; nullsFirst?: boolean }) {
    const dir = opcoes?.ascending === false ? "desc" : "asc";
    const nulos = opcoes?.nullsFirst ? "nulls first" : "nulls last";
    this.ordens.push(`${cita(campo)} ${dir} ${nulos}`);
    return this;
  }

  limit(n: number) {
    this.limite = n;
    return this;
  }

  range(de: number, ate: number) {
    this.deslocamento = de;
    this.limite = ate - de + 1;
    return this;
  }

  single() {
    this.unico = "single";
    return this;
  }
  maybeSingle() {
    this.unico = "maybe";
    return this;
  }
  returns() {
    return this;
  }
  abortSignal() {
    return this;
  }
  throwOnError() {
    return this;
  }

  private montarOnde(parametros: unknown[]): string {
    const partes: string[] = [];
    for (const f of this.filtros) {
      if (f.operador === "bruto") {
        partes.push(f.bruto!);
        continue;
      }
      const negado = f.operador.startsWith("NOT ");
      const op = negado ? f.operador.slice(4) : f.operador;
      let condicao: string;
      if (op === "in") {
        const lista = (f.valor as unknown[]) ?? [];
        if (lista.length === 0) {
          partes.push("false");
          continue;
        }
        const marcas = lista.map((v) => {
          parametros.push(v);
          return `$${parametros.length}`;
        });
        condicao = `${cita(f.campo)} in (${marcas.join(", ")})`;
      } else if (op === "is") {
        condicao =
          f.valor === null
            ? `${cita(f.campo)} is null`
            : `${cita(f.campo)} is ${f.valor ? "true" : "false"}`;
      } else {
        parametros.push(f.valor);
        condicao = `${cita(f.campo)} ${op} $${parametros.length}`;
      }
      partes.push(negado ? `not (${condicao})` : condicao);
    }
    return partes.length ? ` where ${partes.join(" and ")}` : "";
  }

  private async executar(): Promise<Resposta> {
    const parametros: unknown[] = [];
    let sql: string;

    if (this.tabela === "user_roles") {
      if (this.acao === "select") {
        const usuariosComPapel = `(select id::uuid, id::uuid as user_id,
                 case coalesce(nivel, 1)::int
                   when 1 then 'admin'
                   when 2 then 'gerente'
                   when 3 then 'operador'
                   when 4 then 'estagiario'
                   when 5 then 'suspenso'
                   when 6 then 'ferias'
                   else 'admin'
                 end as role
            from public.users) as user_roles_externos`;
        if (this.somenteContagem) {
          const ondeContagem = this.montarOnde(parametros);
          const linhas = await executarSQL(
            `select count(*)::int as total from ${usuariosComPapel}${ondeContagem}`,
            parametros,
          );
          return {
            data: [],
            error: null,
            count: Number(linhas[0]?.["total"] ?? 0),
            status: 200,
            statusText: "OK",
          };
        }
        const onde = this.montarOnde(parametros);
        sql = `select id, user_id, role from ${usuariosComPapel}${onde}`;
        if (this.ordens.length) sql += ` order by ${this.ordens.join(", ")}`;
        if (this.limite != null) sql += ` limit ${this.limite}`;
        if (this.deslocamento) sql += ` offset ${this.deslocamento}`;
        const linhas = await executarSQL(sql, parametros);
        if (this.unico) {
          if (linhas.length === 0 && this.unico === "maybe") {
            return { data: null, error: null, count: 0, status: 200, statusText: "OK" };
          }
          return {
            data: linhas[0] ?? null,
            error: null,
            count: linhas.length,
            status: 200,
            statusText: "OK",
          };
        }
        return {
          data: linhas,
          error: null,
          count: this.contar ? linhas.length : null,
          status: 200,
          statusText: "OK",
        };
      }

      if (this.acao === "insert" || this.acao === "upsert") {
        const atualizadas: Record<string, unknown>[] = [];
        for (const valor of this.valores) {
          const userId = valor["user_id"];
          const role = String(valor["role"] ?? "admin");
          await executarSQL(
            `update public.users set nivel = $2, updated_at = now() where id = $1::uuid`,
            [userId, papelParaNivel(role)],
          );
          atualizadas.push({ id: userId, user_id: userId, role });
        }
        return {
          data: atualizadas,
          error: null,
          count: atualizadas.length,
          status: 200,
          statusText: "OK",
        };
      }

      return { data: [], error: null, count: 0, status: 200, statusText: "OK" };
    }

    if (this.acao === "select") {
      const onde = this.montarOnde(parametros);
      if (this.somenteContagem) {
        const linhas = await executarSQL(
          `select count(*)::int as total from ${qualificar(this.tabela)}${onde}`,
          parametros,
        );
        return {
          data: [],
          error: null,
          count: Number(linhas[0]?.["total"] ?? 0),
          status: 200,
          statusText: "OK",
        };
      }
      sql = `select * from ${qualificar(this.tabela)}${onde}`;
      if (this.ordens.length) sql += ` order by ${this.ordens.join(", ")}`;
      if (this.limite != null) sql += ` limit ${this.limite}`;
      if (this.deslocamento) sql += ` offset ${this.deslocamento}`;
    } else if (this.acao === "insert" || this.acao === "upsert") {
      const campos = [...new Set(this.valores.flatMap((v) => Object.keys(v)))];
      const linhasSql = this.valores.map((v) => {
        const marcas = campos.map((c) => {
          parametros.push(c in v ? this.normalizar(v[c]) : null);
          return `$${parametros.length}`;
        });
        return `(${marcas.join(", ")})`;
      });
      sql =
        `insert into ${qualificar(this.tabela)} (${campos.map(cita).join(", ")}) ` +
        `values ${linhasSql.join(", ")}`;
      if (this.acao === "upsert") {
        const atualiza = campos
          .filter((c) => !this.conflito.includes(c))
          .map((c) => `${cita(c)} = excluded.${cita(c)}`);
        sql += ` on conflict (${this.conflito.map(cita).join(", ")}) ${
          atualiza.length ? `do update set ${atualiza.join(", ")}` : "do nothing"
        }`;
      }
      sql += " returning *";
    } else if (this.acao === "update") {
      const dados = this.valores[0] ?? {};
      const atribuicoes = Object.keys(dados).map((c) => {
        parametros.push(this.normalizar(dados[c]));
        return `${cita(c)} = $${parametros.length}`;
      });
      sql = `update ${qualificar(this.tabela)} set ${atribuicoes.join(", ")}${this.montarOnde(
        parametros,
      )} returning *`;
    } else {
      sql = `delete from ${qualificar(this.tabela)}${this.montarOnde(parametros)} returning *`;
    }

    const linhas = await executarSQL(sql, parametros);
    if (this.acao === "select") {
      const { embutidos } = analisarSelecao(this.selecao);
      await resolverEmbutidos(this.tabela, linhas, embutidos);
    } else if (typeof window !== "undefined") {
      avisarMudancaLocal(this.tabela, this.acao, linhas[0]);
    }

    if (this.unico) {
      if (linhas.length === 0) {
        if (this.unico === "maybe") {
          return { data: null, error: null, count: 0, status: 200, statusText: "OK" };
        }
        return {
          data: null,
          error: { message: "Nenhum registro encontrado." },
          count: 0,
          status: 406,
          statusText: "Not Acceptable",
        };
      }
      return {
        data: linhas[0] ?? null,
        error: null,
        count: linhas.length,
        status: 200,
        statusText: "OK",
      };
    }
    return {
      data: linhas,
      error: null,
      count: this.contar ? linhas.length : null,
      status: 200,
      statusText: "OK",
    };
  }

  private normalizar(v: unknown) {
    if (v && typeof v === "object" && !Array.isArray(v)) return JSON.stringify(v);
    return v;
  }

  then<R1 = Resposta, R2 = never>(
    aoResolver?: ((v: Resposta) => R1 | PromiseLike<R1>) | null,
    aoRejeitar?: ((r: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return this.executar()
      .catch((e: Error) => ({
        data: null,
        error: { message: e.message },
        count: null,
        status: 500,
        statusText: "Error",
      }))
      .then(aoResolver ?? undefined, aoRejeitar ?? undefined);
  }
}

/** Cria uma consulta no banco externo. */
export function fromExterno(tabela: string) {
  return new ConsultaExterna(tabela);
}

/** Executa uma função do banco (RPC) no banco externo. */
export async function rpcExterno(nome: string, argumentos?: Record<string, unknown>) {
  if (nome === "excluir_vendas_data") {
    const usuarioId =
      typeof window !== "undefined"
        ? (window as any).__OBTER_DADOS_AUTH?.user?.id ?? null
        : null;
    try {
      const linhas = await executarSQL(
        `select * from public.excluir_vendas_data($1::date, $2::integer, $3::uuid)`,
        [argumentos?.["_data"], argumentos?.["_ordem"], usuarioId],
      );
      return { data: Number(linhas[0]?.["excluir_vendas_data"] ?? 0), error: null, count: null, status: 200, statusText: "OK" };
    } catch (e) {
      return { data: null, error: { message: (e as Error).message }, count: null, status: 500, statusText: "Error" };
    }
  }

  if (nome === "role_level") {
    try {
      const linhas = await executarSQL(
        `select coalesce(nivel, 1)::int as nivel from public.users where id = $1::uuid limit 1`,
        [argumentos?.["_user_id"]],
      );
      return {
        data: Number(linhas[0]?.["nivel"] ?? 9),
        error: null,
        count: null,
        status: 200,
        statusText: "OK",
      };
    } catch (e) {
      return {
        data: null,
        error: { message: (e as Error).message },
        count: null,
        status: 500,
        statusText: "Error",
      };
    }
  }

  if (nome === "has_role") {
    try {
      const linhas = await executarSQL(
        `select coalesce(nivel, 1)::int as nivel from public.users where id = $1::uuid limit 1`,
        [argumentos?.["_user_id"]],
      );
      const papel = nivelParaPapel(linhas[0]?.["nivel"]);
      return {
        data: papel === String(argumentos?.["_role"] ?? ""),
        error: null,
        count: null,
        status: 200,
        statusText: "OK",
      };
    } catch (e) {
      return {
        data: null,
        error: { message: (e as Error).message },
        count: null,
        status: 500,
        statusText: "Error",
      };
    }
  }

  // Equivalente à função listar_colunas_geradas() da nuvem: aponta as colunas
  // calculadas pelo banco, que não podem aparecer em comandos de gravação.
  if (nome === "listar_colunas_geradas") {
    try {
      const linhas = await executarSQL(
        `select table_schema::text as esquema, table_name::text as tabela, column_name::text as campo
           from information_schema.columns
          where table_schema in ('public')
            and (is_generated = 'ALWAYS' or identity_generation = 'ALWAYS')
          order by table_schema, table_name, ordinal_position`,
      );
      return { data: linhas, error: null, count: null, status: 200, statusText: "OK" };
    } catch (e) {
      return {
        data: null,
        error: { message: (e as Error).message },
        count: null,
        status: 500,
        statusText: "Error",
      };
    }
  }

  // Equivalente à função exportar_auth_tabela(texto) da nuvem: devolve todos os
  // registros de uma tabela de contas em formato json. Tabela ausente (como
  // public.identities em alguns servidores) devolve lista vazia, sem travar a exportação.
  if (nome === "exportar_auth_tabela") {
    const tabela = String(argumentos?.["_tabela"] ?? "");
    if (tabela !== "users" && tabela !== "identities") {
      return {
        data: null,
        error: { message: `Tabela não permitida: ${tabela}` },
        count: null,
        status: 400,
        statusText: "Bad Request",
      };
    }
    try {
      const linhas = await executarSQL(
        `select coalesce(jsonb_agg(to_jsonb(t)), '[]'::jsonb) as dados from ${qualificar(
          tabela,
        )} t`,
      );
      return {
        data: linhas[0]?.["dados"] ?? [],
        error: null,
        count: null,
        status: 200,
        statusText: "OK",
      };
    } catch (e) {
      const erro = e as Error;
      if (erro.message.includes("42P01") || erro.message.toLowerCase().includes("does not exist")) {
        return { data: [], error: null, count: null, status: 200, statusText: "OK" };
      }
      return {
        data: null,
        error: { message: erro.message },
        count: null,
        status: 500,
        statusText: "Error",
      };
    }
  }

  const parametros: unknown[] = [];
  const nomes = Object.keys(argumentos ?? {});
  const lista = nomes.map((n) => {
    const valor = argumentos![n];
    parametros.push(valor && typeof valor === "object" ? JSON.stringify(valor) : valor);
    return `${cita(n)} => $${parametros.length}`;
  });
  const sql = `select * from ${qualificar(nome)}(${lista.join(", ")})`;
  try {
    const linhas = await executarSQL(sql, parametros);
    const chaves = linhas.length ? Object.keys(linhas[0]!) : [];
    if (chaves.length === 1) {
      const k = chaves[0]!;
      const valores = linhas.map((l) => l[k]);
      return {
        data: valores.length === 1 ? valores[0] : valores,
        error: null,
        count: null,
        status: 200,
        statusText: "OK",
      };
    }
    return { data: linhas, error: null, count: null, status: 200, statusText: "OK" };
  } catch (e) {
    return {
      data: null,
      error: { message: (e as Error).message },
      count: null,
      status: 500,
      statusText: "Error",
    };
  }
}
